import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'react-redux';
import html2canvas from 'html2canvas';
import Swal from 'sweetalert2';
import { useSignalR } from '../../hooks/useSignalR';
import api from '../../services/api';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';
import './Header.css';

const Header = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  const store = useStore();
  const { subscribe, unsubscribe } = useSignalR();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  // Load actual unread count from backend on mount
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const response = await api.get('/issuereports/unread-count');
        if (response.data?.success && response.data?.data !== undefined) {
          setUnreadCount(response.data.data);
          console.log('[Header] Loaded initial unread count:', response.data.data);
        }
      } catch (error) {
        console.error('[Header] Error loading unread count:', error);
        // Keep count at 0 on error
      }
    };

    loadUnreadCount();
  }, []);

  // Setup SignalR event listeners for notification count
  useEffect(() => {
    // Handler for unread count update from backend (most reliable)
    const handleUnreadCountUpdate = (count) => {
      console.log('[Header] Received unread count update from server:', count);
      setUnreadCount(count);
    };

    // Handler for new messages (fallback - refresh count from server)
    const handleNewMessage = async () => {
      console.log('[Header] Received new message notification');
      // Request fresh count from server
      try {
        const response = await api.get('/issuereports/unread-count');
        if (response.data?.success && response.data?.data !== undefined) {
          setUnreadCount(response.data.data);
        }
      } catch (error) {
        console.error('[Header] Error refreshing unread count:', error);
      }
    };

    // Subscribe to real-time events
    subscribe('UnreadCountUpdate', handleUnreadCountUpdate);
    subscribe('NewMessage', handleNewMessage);

    // Cleanup subscriptions
    return () => {
      unsubscribe('UnreadCountUpdate', handleUnreadCountUpdate);
      unsubscribe('NewMessage', handleNewMessage);
    };
  }, [subscribe, unsubscribe]);

  // Navigate to issue reports (don't reset count - let backend control it)
  const handleNotificationClick = () => {
    navigate('/issue-reports');
  };

  // Handle report issue button click
  const handleReportIssueClick = async () => {
    try {
      Swal.fire({
        title: 'Capturing Screenshot...',
        html: 'Please wait while we capture the screen...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: 'Capture Failed',
        text: 'Failed to capture screenshot. Please try again.'
      });
    }
  };

  return (
    <div className="app-header">
      <button type="button" className="menu-toggle" onClick={onMenuToggle}>
        ☰
      </button>

      <div className="header-actions">
        <button
          type="button"
          className="notification-bell"
          onClick={handleNotificationClick}
          title="View notifications"
        >
          <i className="fa fa-bell"></i>
          {unreadCount > 0 && (
            <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </button>

        <button
          type="button"
          className="report-issue-btn"
          onClick={handleReportIssueClick}
          title="Laporkan Masalah / Report Issue"
        >
          <i className="fas fa-bug"></i>
        </button>
      </div>

      {/* KTC Logo at bottom */}
      <div className="header-logo" title="Kim Teck Cheong Consolidated Bhd">
        <img
          src="/img/ktc-logo2.jpg"
          alt="Kim Teck Cheong Consolidated Bhd"
          className="header-logo-img"
        />
      </div>

      {/* Report Issue Modal */}
      {showIssueModal && (
        <ReportIssueModal
          context={{}}
          screenshot={screenshot}
          onClose={() => setShowIssueModal(false)}
          reduxStore={store}
        />
      )}
    </div>
  );
};

export default Header;
